import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TreeTaskCard } from 'shared/components/connected/task-card';
import { ListSectionLifeAreaHeader } from 'shared/components/ui/section-header-life-area';
import { GoalHeader } from 'shared/components/ui/task-card-task-section/goal-header';
import {
  RenderItemProps,
  rootId,
  TreeItem,
  TreeItemTypes,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { lifeAreaOtherID } from 'shared/types/orderable-section';
import { Task } from 'shared/types/task';

import { useLifeAreaTree } from '../../hooks/use-life-area-tree';
import { GroupedListProps } from '../../types';
import * as Styled from './life-area-grouping-tasks-list.style';

const taskTreeItemTypes = [TreeItemTypes.Task];

export const LifeAreaGroupingTasksList: React.FC<GroupedListProps> = ({
  tasks,
  lifeAreas,
  goals,
  sortingMode,
  onReorder,
  customOrder = {},
  collapsedSections = [],
  onSectionCollapse,
  weekStartsOn,
  scrollElement,
}) => {
  const { t } = useTranslation();
  const today = useToday();

  const lifeAreasMap = useMemo(
    () =>
      lifeAreas.reduce<Record<ID, LifeArea>>((acc, lifeArea) => {
        acc[lifeArea.id] = lifeArea;
        return acc;
      }, {}),
    [lifeAreas],
  );
  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  const treeData = useLifeAreaTree({
    lifeAreas,
    goals,
    tasks,
    customOrder,
    sortingMode,
    today,
    weekStartsOn,
  });

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }

    const updatedDocs: Task[] = [];
    const updatedSections: { id: ID; taskSorting: ID[] }[] = [];
    updatedItems.forEach((item) => {
      if (item.type === TreeItemTypes.Task) {
        if (!tasksMap[item.id]) {
          return;
        }

        const taskToUpdate = structuredClone(tasksMap[item.id]);
        const lifeAreaItem = updatedItems.find(({ children }) =>
          children?.includes(item.id),
        );

        taskToUpdate.lifeAreaId =
          !lifeAreaItem?.id || lifeAreaItem.id === lifeAreaOtherID
            ? null
            : lifeAreaItem.id;
        taskToUpdate.goalId = null;
        updatedDocs.push(taskToUpdate);
      }
      if (item.type === TreeItemTypes.LifeArea) {
        if (!lifeAreasMap[item.id]) {
          return;
        }
        updatedSections.push({ id: item.id, taskSorting: item.children ?? [] });
      }
    });

    onReorder(updatedSections, updatedDocs);
  };

  const renderItem: React.FC<RenderItemProps> = ({
    item,
    itemInstance,
    tree,
  }) => {
    const type = item.type;

    // Task
    if (type === TreeItemTypes.Task) {
      return tasksMap[item.sourceId] ? (
        <TreeTaskCard
          task={tasksMap[item.sourceId]}
          tree={tree}
          item={itemInstance}
          tasks={tasks}
        />
      ) : null;
    }

    // LifeArea
    if (type === TreeItemTypes.LifeArea) {
      const lifeArea: LifeArea | undefined = lifeAreasMap[item.sourceId];
      return (
        <Styled.HeaderContainer>
          <ListSectionLifeAreaHeader
            id={item.sourceId}
            name={
              lifeArea?.name ?? t('general.sections.life-areas.other.title')
            }
            color={lifeArea?.color ?? undefined}
            isFrozen={!!lifeArea?.frozenAt}
            image={lifeArea?.visionPreview?.image}
          />
        </Styled.HeaderContainer>
      );
    }

    // Goal
    if (type === TreeItemTypes.Goal) {
      return (
        <Styled.HeaderContainer>
          <GoalHeader
            goalId={item.sourceId}
            goals={goals}
            lifeAreas={lifeAreas}
            onPremium={() => {}}
          />
        </Styled.HeaderContainer>
      );
    }

    return null;
  };

  return (
    <VirtualizedTreeList
      rootItemId={rootId}
      items={treeData}
      renderItem={renderItem}
      scrollElement={scrollElement}
      multiselectTypes={taskTreeItemTypes}
      onReorder={_onReorder}
      collapsedItems={collapsedSections}
      onCollapse={onSectionCollapse}
    />
  );
};
